import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useChampionship } from "@/context/ChampionshipContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const emptyContact = { name: "", position: "", contact_link: "", profile_url: "" };
const blankGroup = { title: "", description: "", order: 0, contacts: [] };

export default function AdminContactsPage() {
  const { current } = useChampionship() || {};
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankGroup);

  const load = useCallback(async () => {
    if (!current?.id) return;
    const { data } = await api.get(`/championships/${current.id}/contact-groups`);
    setGroups(data || []);
  }, [current?.id]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(blankGroup); setOpen(true); };
  const openEdit = (g) => {
    setEditing(g);
    // Assign a stable client-side id to each contact so list keys are unique
    const withIds = (g.contacts || []).map((c, i) => ({
      _id: c._id || `${g.id}-${i}-${c.name || "x"}`,
      ...c,
    }));
    setForm({ title: g.title, description: g.description || "", order: g.order || 0, contacts: withIds });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) await api.patch(`/contact-groups/${editing.id}`, form);
      else await api.post(`/championships/${current.id}/contact-groups`, form);
      toast.success("Saved");
      setOpen(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this contact group?")) return;
    try { await api.delete(`/contact-groups/${id}`); toast.success("Deleted"); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const addContact = () => setForm({ ...form, contacts: [...form.contacts, { ...emptyContact }] });
  const updContact = (i, field, val) => {
    const list = [...form.contacts];
    list[i] = { ...list[i], [field]: val };
    setForm({ ...form, contacts: list });
  };
  const rmContact = (i) => setForm({ ...form, contacts: form.contacts.filter((_, k) => k !== i) });

  return (
    <div className="p-8 lg:p-10" data-testid="admin-contacts">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Organisation</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-2">Contact groups</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage what appears on the public Contact page, grouped by team/role.</p>
        </div>
        <Button onClick={openNew} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="contact-new-button">
          <Plus className="h-4 w-4 mr-2" /> New group
        </Button>
      </div>

      <div className="space-y-4">
        {groups.length === 0 && <div className="text-muted-foreground">No contact groups yet.</div>}
        {groups.map((g) => (
          <div key={g.id} className="border border-border bg-white p-5" data-testid={`contact-group-row-${g.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-display text-xl font-bold">{g.title}</div>
                {g.description && <div className="text-sm text-muted-foreground mt-1">{g.description}</div>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => openEdit(g)}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(g.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">{(g.contacts || []).length} contact(s) · order {g.order}</div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit contact group" : "New contact group"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Group title (e.g. "Tournament Group")</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="contact-group-title" />
            </div>
            <div className="space-y-2">
              <Label>Description / contact line (optional)</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Email: ... · Phone: ..." data-testid="contact-group-desc" />
            </div>
            <div className="space-y-2">
              <Label>Order</Label>
              <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="w-32" />
            </div>

            <div className="pt-2 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <div className="font-medium">Contacts</div>
                <Button size="sm" variant="outline" onClick={addContact} data-testid="add-contact-row"><Plus className="h-3.5 w-3.5 mr-1" /> Add person</Button>
              </div>
              <div className="space-y-3">
                {form.contacts.map((c, i) => (
                  <div key={c._id || `new-${i}`} className="border border-border p-3 space-y-2" data-testid={`contact-edit-row-${i}`}>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Name" value={c.name} onChange={(e) => updContact(i, "name", e.target.value)} data-testid={`contact-name-${i}`} />
                      <Input placeholder="Position / role" value={c.position} onChange={(e) => updContact(i, "position", e.target.value)} data-testid={`contact-position-${i}`} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Contact link (mailto: or https://)" value={c.contact_link} onChange={(e) => updContact(i, "contact_link", e.target.value)} data-testid={`contact-link-${i}`} />
                      <Input placeholder="Profile URL (optional)" value={c.profile_url} onChange={(e) => updContact(i, "profile_url", e.target.value)} />
                    </div>
                    <div className="flex justify-end">
                      <Button size="sm" variant="ghost" onClick={() => rmContact(i)} className="text-destructive">
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                      </Button>
                    </div>
                  </div>
                ))}
                {form.contacts.length === 0 && (
                  <div className="text-sm text-muted-foreground">No contacts yet — add at least one person above.</div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="contact-group-save">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
